// apps/web/src/pages/estoque/inventarios-page.tsx — Fase 13, Plano 27 (EST-13, D-24..D-26).
// Lista de inventários (`/estoque/inventarios`): Inventário · Localização · Dono · Situação · Itens contados · Contagem por ·
// Criado em. "Novo inventário" só para quem tem estoque:inventariar; a autorização real é do servidor.
import { useCallback, useEffect, useState } from 'react';
import { Building2, CheckCircle2, Hourglass, ListChecks, RefreshCw, User, XCircle } from 'lucide-react';
import { SITUACOES_INVENTARIO, type DonoTipo, type InventarioResumo, type ListaInventarios, type SituacaoInventario } from '@pieltec/contracts';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import {
  PERMISSAO_INVENTARIAR,
  TAMANHO_PAGINA_INVENTARIOS,
  apresentacaoSituacaoInventario,
  contagemInventarios,
  escreverFiltrosInventariosNaUrl,
  filtrosInventariosAtivos,
  filtrosInventariosVazios,
  lerFiltrosInventariosDaUrl,
  montarQueryInventarios,
  urlDetalheInventario,
  type FiltrosInventarios,
  type IconeSituacaoInventario,
} from '../../lib/estoque/inventario';
import { obterSessaoAtual } from '../../lib/session';
import { formatarDataHora } from '../../lib/vendas/formatacao';
import { NovoInventarioSheet } from './novo-inventario-sheet';
import { CampoSelect } from '../../components/ui/campo-select';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const ICONES_SITUACAO: Record<IconeSituacaoInventario, typeof ListChecks> = { ListChecks, Hourglass, CheckCircle2, XCircle };

function LinhaInventario({ inventario }: { inventario: InventarioResumo }) {
  const situacao = apresentacaoSituacaoInventario(inventario.situacao);
  const Icone = ICONES_SITUACAO[situacao.icone];
  const autores = inventario.autoresContagem.map((autor) => autor.nome).join(', ');
  return (
    <tr className="border-t hover:bg-slate-50">
      <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">{inventario.numero}</td>
      <td className="px-3 py-2">{inventario.localizacaoNome}</td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium text-slate-700 ${
            inventario.donoTipo === 'PIELTEC' ? 'border-slate-200 bg-slate-100' : 'border-slate-300 bg-white'
          }`}
        >
          {inventario.donoTipo === 'PIELTEC' ? <Building2 className="size-3.5" aria-hidden="true" /> : <User className="size-3.5" aria-hidden="true" />}
          {inventario.donoNome}
        </span>
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
          <Icone className="size-3.5 shrink-0" aria-hidden="true" />
          {situacao.rotulo}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
        {inventario.itensContados} de {inventario.totalItens}
      </td>
      <td className="max-w-[16rem] truncate px-3 py-2" title={autores || undefined}>
        {autores || '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatarDataHora(inventario.criadoEm)}</td>
      <td className="px-3 py-2 text-right">
        <Button asChild variant="outline" size="sm" className="min-h-11 sm:min-h-8">
          <a href={urlDetalheInventario(inventario.id)} aria-label={`Abrir inventário ${inventario.numero}`}>
            Abrir
          </a>
        </Button>
      </td>
    </tr>
  );
}

export function InventariosPage() {
  const [filtros, setFiltros] = useState<FiltrosInventarios>(() => lerFiltrosInventariosDaUrl(window.location.search));
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_INVENTARIOS);
  const [dados, setDados] = useState<ListaInventarios | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [sheetAberto, setSheetAberto] = useState(false);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/estoque/inventarios?${montarQueryInventarios(filtros, { pagina, tamanhoPagina })}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaInventarios;
        if (!sinal || sinal.vivo) setDados(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina],
  );

  // Filtros persistem na URL (substitui a entrada do histórico: filtrar não empilha voltas).
  useEffect(() => {
    window.history.replaceState(null, '', `${window.location.pathname}${escreverFiltrosInventariosNaUrl(filtros)}`);
  }, [filtros]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function alterarFiltros(parcial: Partial<FiltrosInventarios>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function limparFiltros(): void {
    setFiltros(filtrosInventariosVazios());
    setPagina(1);
  }

  const podeInventariar = permissoes.includes(PERMISSAO_INVENTARIAR);
  const comFiltro = filtrosInventariosAtivos(filtros);
  const vazio = !carregando && !erro && (dados?.itens.length ?? 0) === 0;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Inventários</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {dados ? contagemInventarios(dados.total) : ' '}
            </p>
          </div>
          {podeInventariar && (
            <Button type="button" onClick={() => setSheetAberto(true)} className="min-h-11 sm:min-h-9">
              Novo inventário
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="sticky top-0 z-20 flex flex-wrap items-end gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-situacao" className={ROTULO}>
            Situação
          </label>
          <CampoSelect
            id="filtro-situacao"
            value={filtros.situacao}
            onValueChange={(valor) => alterarFiltros({ situacao: valor as SituacaoInventario | '' })}
            options={[{ value: '', label: 'Todas as situações' }, ...SITUACOES_INVENTARIO.map((situacao) => ({ value: situacao, label: apresentacaoSituacaoInventario(situacao).rotulo }))]}
            className="w-56"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-localizacao" className={ROTULO}>
            Localização
          </label>
          <LocalizacaoSelect
            id="filtro-localizacao"
            rotulo="Localização"
            incluirTodas
            value={filtros.localizacaoId}
            onChange={(id) => alterarFiltros({ localizacaoId: id })}
            className="min-w-48"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-dono" className={ROTULO}>
            Dono
          </label>
          <CampoSelect
            id="filtro-dono"
            value={filtros.donoTipo}
            onValueChange={(valor) => alterarFiltros({ donoTipo: valor as DonoTipo | '' })}
            options={[{ value: '', label: 'Todos' }, { value: 'PIELTEC', label: 'Pieltec' }, { value: 'CLIENTE', label: 'Cliente' }]}
            className="w-40"
          />
        </div>

        <label htmlFor="filtro-encerrados" className="flex min-h-11 cursor-pointer items-center gap-2 text-sm sm:min-h-[var(--control-height,2rem)]">
          <Checkbox
            id="filtro-encerrados"
            checked={filtros.incluirEncerrados}
            onCheckedChange={(marcada) => alterarFiltros({ incluirEncerrados: marcada === true })}
            className="size-5 sm:size-4"
          />
          Incluir ajustados e descartados
        </label>

        {comFiltro && (
          <Button type="button" variant="ghost" className="min-h-11 sm:min-h-9" onClick={limparFiltros}>
            Limpar filtros
          </Button>
        )}
      </div>

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar os inventários. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando inventários">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : vazio && comFiltro ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum inventário encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : vazio ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum inventário</h2>
            <p className="max-w-xl text-sm text-muted-foreground">Crie um inventário para contar uma localização. A primeira contagem de cada local vira o saldo de abertura.</p>
            {podeInventariar && (
              <Button type="button" onClick={() => setSheetAberto(true)} className="min-h-11 sm:min-h-9">
                Novo inventário
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <caption className="sr-only">Inventários por localização e dono</caption>
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Inventário</th>
                <th scope="col" className={CABECALHO}>Localização</th>
                <th scope="col" className={CABECALHO}>Dono</th>
                <th scope="col" className={CABECALHO}>Situação</th>
                <th scope="col" className={CABECALHO}>Itens contados</th>
                <th scope="col" className={CABECALHO}>Contagem por</th>
                <th scope="col" className={CABECALHO}>Criado em</th>
                <th scope="col" className="w-20 px-3 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(dados?.itens ?? []).map((inventario) => (
                <LinhaInventario key={inventario.id} inventario={inventario} />
              ))}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={dados?.total ?? 0}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="inventários"
          />
        </Card>
      )}

      {podeInventariar && <NovoInventarioSheet aberto={sheetAberto} aoFechar={() => setSheetAberto(false)} />}
    </div>
  );
}
